import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import Modal from 'apollo-react/components/Modal';
import qs from 'query-string';
import React, { useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import ErrorContainer from './ErrorContainer';
import { closeDeleteModal, deleteRecord } from '../../actions/MasterSetupPageActions';
import { clearWorkflow } from '../../actions/WorkflowActions';
import { displayStepForm } from '../../actions/WorkflowStepPageActions';
import CustomLoader from '../../components/CustomLoader';
import Layout from '../../components/Layout';
import PopupWindowDialog from '../../components/PopupWindowDialog';
import withSubmitFormDialog from '../../components/withSubmitFormDialog';
import * as Urls from '../../constants/Urls';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { clearFormDesigner } from '../../form-designer/actions/form-manager.actions';
import FormFooter from '../../form-designer/components/FormFooter';
import FormHeader from '../../form-designer/components/FormHeader';
import MobileFormHeader from '../../form-designer/components/MobileFormHeader';
import PreventMountMessage from '../../form-designer/components/PreventMountMessage';
import StepForm from '../../form-designer/components/StepForm';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { FormContext } from '../../form-designer/context';
import {
  isFormLoading,
  isMasterSetupLoading,
} from '../../form-designer/selectors/common.selectors';
import { getIsMasterSetupFormViewOnly } from '../../form-designer/selectors/form-manager.selectors';
import { getContainerWidthPercentBasedOnSelectedSubGrids } from '../../form-designer/selectors/runtime.selectors';
import { clearValidationStorage } from '../../form-designer/utils/validationStorage.utils';
import { getPreventFormMounting } from '../../selectors/AppConfigsSelectors';
import {
  getDeleteModalMessageTranslation,
  getIsDeleteModalOpen,
  getRedirect,
} from '../../selectors/MasterSetupPageSelectors';
import { getIsWorkflowRequestPending } from '../../selectors/WorkflowSelectors';
import { getPagePathname } from '../../utils/childrenWindowsUtils';
import { getUITA } from '../../utils/commonUtils';
import AttachmentPreviewDialog from '../EntityDisplay/AttachmentPreviewDialog';

const useStyles = makeStyles({
  formWrapperStyled: {
    backgroundColor: '#fff',
    height: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    padding: ({ isMobileDevice }) => `0 ${isMobileDevice ? 8 : 32}px 0 8px`,
    width: ({ widthPercent }) => `${widthPercent}%`,
  },
  wrapperStyled: {
    display: 'flex',
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
  },
  stepFormContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 650px',
    width: 0,
    height: 'calc(100vh - 56px)',
  },
  innerFormContainer: { flex: '0 1 635px' },
});

function WorkflowStepPage({ location }) {
  const { isMobileDevice } = useWindowDimensions();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const widthPercent = useFormSelector(getContainerWidthPercentBasedOnSelectedSubGrids, {
    pathname: location.pathname,
    formKey: FORM_KEYS.STEP,
    isMobileDevice,
  });
  const isMasterSetupViewOnly = useFormSelector(getIsMasterSetupFormViewOnly, {
    formKey: FORM_KEYS.STEP,
  });

  const classes = useStyles({ widthPercent, isMobileDevice });
  const isMasterLoading = useSelector(isMasterSetupLoading);
  const isModalOpen = useSelector(getIsDeleteModalOpen);
  const modalMessage = useSelector(getDeleteModalMessageTranslation);
  const redirect = useSelector(getRedirect);
  const { entityName, searchName } = useSelector((state) => state.masterSetupPage);
  const showLoader = useSelector(isFormLoading(FORM_KEYS.STEP));
  const isWorkflowRequestPending = useSelector(getIsWorkflowRequestPending);
  const preventFormMounting = useSelector(getPreventFormMounting);

  const pathname = getPagePathname();

  useEffect(() => {
    const qsParams = qs.parse(location.search) || {};

    dispatch(
      displayStepForm({
        entityViewName: qsParams.entityViewName,
        entityType: qsParams.entityType,
        containerWorkflowKey: qsParams.containerWorkflowKey,
        workflowKey: qsParams.workflowKey,
        stepId: qsParams.stepId,
        itmId: qsParams.itmId,
        formKey: qsParams.formKey,
        entityName: qsParams.entityName,
        storeKey: FORM_KEYS.STEP,
        workflowId: qsParams.workflowId,
      })
    );

    return () => {
      if ([Urls.TRAININGS].includes(pathname)) {
        return;
      }
      sessionStorage.removeItem('backButtonURL');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(
    () => () => {
      batch(() => {
        dispatch(clearFormDesigner(FORM_KEYS.STEP));
        dispatch(clearWorkflow());
        clearValidationStorage();
      });
    },
    [dispatch, location]
  );

  if (redirect) {
    const qsParams = qs.parse(location.search) || {};
    return (
      <Redirect
        to={
          entityName
            ? `${Urls.MASTER_SETUP_PAGE}?entityName=${entityName}&searchName=${searchName}`
            : qsParams.entityName
            ? `${Urls.MASTER_SETUP_PAGE}?entityName=${qsParams.entityName}&searchName=${qsParams.searchName}`
            : Urls.HOME
        }
      />
    );
  }

  return (
    <Layout>
      <FormContext.Provider value={FORM_KEYS.STEP}>
        <div className={classes.wrapperStyled}>
          {!isMobileDevice && <ErrorContainer />}
          <div
            className={classes.stepFormContainer}
            // style={{ height: 'calc(100vh - 64px)' }}
            ui-ta="stepFormContainer"
          >
            {preventFormMounting ? (
              <PreventMountMessage />
            ) : (
              <>
                <div className={classes.formWrapperStyled} ui-ta="ScrollingElement">
                  {showLoader ? (
                    <CustomLoader />
                  ) : (
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        {isMobileDevice ? <MobileFormHeader /> : <FormHeader />}
                      </Grid>
                      <Grid item xs={12}>
                        <StepForm isViewOnly={isMasterSetupViewOnly} />
                      </Grid>
                    </Grid>
                  )}
                </div>
                {(isMasterLoading || isWorkflowRequestPending) && <Loader isInner />}
                {!showLoader && <FormFooter />}
              </>
            )}
          </div>
          <AttachmentPreviewDialog />
          <PopupWindowDialog />
          <Modal
            open={isModalOpen}
            onClose={() => dispatch(closeDeleteModal())}
            variant="warning"
            title={translateString('Warning')}
            message={modalMessage}
            disableBackdropClick
            disableEscapeKeyDown
            PaperProps={{ 'ui-ta': getUITA('ConfirmPopupElement') }}
            buttonProps={[
              {
                label: translateString('Cancel'),
                'ui-ta': getUITA('PopupButtonCANCEL'),
              },
              {
                color: 'primary',
                // disabled: isLoading,
                label: translateString('Ok'),
                onClick: () => dispatch(deleteRecord()),
                'ui-ta': getUITA('PopupButtonOK'),
              },
            ]}
          />
        </div>
      </FormContext.Provider>
    </Layout>
  );
}

const EnhancedComponent = withSubmitFormDialog(WorkflowStepPage);

export default EnhancedComponent;
